import { lazy, ReactNode, Suspense } from "react";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { ThemeProvider } from "styled-components";
import { useStyles } from "./core/styles/theme";
import initI18n, {
  useI18nChangeLanguageHook,
} from "./core/translations/i18n.config";
import { useUpdateApp } from "./core/utils/useRefreshOutdatedApp";
import { appointmentRouteChildren } from "./features/appointment/AppointmentRouteChildren";
import { AppointmentLayout } from "./features/navigation/AppointmentLayout";
import { InAppLayout } from "./features/navigation/InAppLayout";
import { InviteLayout } from "./features/navigation/InviteLayout";
import { LoadingScreen } from "./features/navigation/LoadingScreen";
import { MainLayout } from "./features/navigation/MainLayout";
import { WelcomeLayout } from "./features/navigation/WelcomeLayout";
import { FormLayout } from "./features/onboarding/FormLayout";
import { SuccessScreen } from "./features/onboarding/SuccessScreen";
import { FormsDisplayScreen } from "./features/practiceForms/FormsDisplayScreen";
import { ErrorOverlay } from "./features/sentry/screens/ErrorOverlay";
import { SentryRouteErrorFallback } from "./features/sentry/screens/SentryRouteErrorFallback";
import { CheckInInfoScreen } from "./features/welcome/screens/CheckInInfoScreen";
import { OnboardingInfoScreen } from "./features/welcome/screens/OnboardingInfoScreen";
import { ReleaseDoneScreen } from "./features/welcome/screens/ReleaseDoneScreen";

// The login screens carry libphonenumber-js and its metadata, which no other page needs at start.
const OnboardingLoginScreen = lazy(() =>
  import("./features/welcome/screens/OnboardingLoginScreen").then((module) => ({
    default: module.OnboardingLoginScreen,
  })),
);
const SmsCodeScreen = lazy(() =>
  import("./features/welcome/screens/SmsCodeScreen").then((module) => ({ default: module.SmsCodeScreen })),
);
const PracticePatientChooserScreen = lazy(() =>
  import("./features/welcome/screens/PracticePatientChooserScreen").then((module) => ({
    default: module.PracticePatientChooserScreen,
  })),
);
const OtherPersonScreen = lazy(() =>
  import("./features/welcome/screens/OtherPersonScreen").then((module) => ({ default: module.OtherPersonScreen })),
);
const ReleaseCodeScreen = lazy(() =>
  import("./features/welcome/screens/ReleaseCodeScreen").then((module) => ({ default: module.ReleaseCodeScreen })),
);
const ReleaseBirthdayScreen = lazy(() =>
  import("./features/welcome/screens/ReleaseBirthdayScreen").then((module) => ({
    default: module.ReleaseBirthdayScreen,
  })),
);
const StaffFillScreen = lazy(() =>
  import("./features/welcome/screens/StaffFillScreen").then((module) => ({ default: module.StaffFillScreen })),
);
const withLoading = (element: ReactNode) => <Suspense fallback={<LoadingScreen />}>{element}</Suspense>;

export default function App() {
  initI18n();
  useI18nChangeLanguageHook();
  useUpdateApp();
  const theme = useStyles();

  const welcomeRouter = {
    path: "/welcome",
    element: <WelcomeLayout />,
    children: [
      {
        path: "",
        element: <OnboardingInfoScreen />,
      },
      {
        path: "login",
        element: withLoading(<OnboardingLoginScreen />),
      },
      {
        path: "code",
        element: withLoading(<SmsCodeScreen />),
      },
      {
        path: "select",
        element: withLoading(<PracticePatientChooserScreen />),
      },
      {
        path: "other-person",
        element: withLoading(<OtherPersonScreen />),
      },
      {
        path: "checkInOnboarding",
        element: <CheckInInfoScreen />,
      },
      {
        path: "success",
        element: <SuccessScreen />,
      },
    ],
  };

  // The practice release (PF-6133) in the welcome screens' layout, which sends a tab with a record to /form.
  const releaseRouter = {
    path: "/release",
    element: <WelcomeLayout />,
    children: [
      {
        path: "code",
        element: withLoading(<ReleaseCodeScreen />),
      },
      {
        path: "birthday",
        element: withLoading(<ReleaseBirthdayScreen />),
      },
      {
        path: "staff",
        element: withLoading(<StaffFillScreen />),
      },
      {
        path: "done",
        element: <ReleaseDoneScreen />,
      },
    ],
  };

  const inviteRouter = {
    path: "/invite",
    element: <InviteLayout />,
  };

  const formRouter = {
    path: "form",
    element: <FormLayout />,
    children: [
      {
        path: "",
        element: <FormsDisplayScreen />,
      },
    ],
  };
  const inAppRouter = {
    path: "",
    element: <InAppLayout />,
    children: [formRouter],
  };
  const appointmentRouter = {
    path: "appointment/",
    element: <AppointmentLayout />,
    children: appointmentRouteChildren,
  };

  const router = createBrowserRouter([
    {
      errorElement: <SentryRouteErrorFallback />,
      element: <MainLayout />,
      hydrateFallbackElement: <ErrorOverlay type={"404"} />,
      children: [
        appointmentRouter,
        welcomeRouter,
        releaseRouter,
        inviteRouter,
        inAppRouter,
        { path: "*", element: <ErrorOverlay type={"404"} /> },
      ],
    },
  ]);

  return (
    <ThemeProvider theme={theme}>
      <RouterProvider router={router} />
    </ThemeProvider>
  );
}
